import { mapValues } from 'lodash-es';
import type { Passenger, PassengersData } from '@aviata/booking-data';
import type { Airflow } from '@aviata/platform-types';
import { dateUtils, citizenshipEntity } from 'shared';

const specialSymbolsRegex = /[- ]/g;
const AIRLINE_BONUS_CARDS_STORAGE_KEY = 'free-avia:passengersAirlineBonusCards';

type MilesCardsMap = Record<string, string>;

export const checkSpecialSymbolsInInitials = (
  passengersState: PassengersData,
) => {
  return Object.values(passengersState).some(pax => {
    return (
      specialSymbolsRegex.test(pax.firstName) ||
      specialSymbolsRegex.test(pax.lastName)
    );
  });
};

export const removeSpecialSymbolsFromInitials = (
  passengersState: PassengersData,
) => {
  return mapValues(passengersState, pax => {
    return {
      ...pax,
      firstName: pax.firstName.replace(/[- ]/g, ''),
      lastName: pax.lastName.replace(/[- ]/g, ''),
    };
  });
};

const readMilesCardsMap = (): MilesCardsMap => {
  try {
    // Единый источник правды по мильным картам: passengerId -> номер карты.
    const raw = localStorage.getItem(AIRLINE_BONUS_CARDS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? parsed : {};
  } catch {
    return {};
  }
};

const writeMilesCardsMap = (map: MilesCardsMap) => {
  localStorage.setItem(AIRLINE_BONUS_CARDS_STORAGE_KEY, JSON.stringify(map));
};

const getMilesCardNumberByPassengerId = (passengerId: string) => {
  return readMilesCardsMap()[passengerId] ?? '';
};

const removeMilesCardByPassengerId = (passengerId: string) => {
  const currentMap = readMilesCardsMap();
  delete currentMap[passengerId];
  writeMilesCardsMap(currentMap);
};

const upsertMilesCardByPassengerId = (passengerId: string, value: string) => {
  const trimmedValue = value.trim();
  const currentMap = readMilesCardsMap();

  if (!trimmedValue) {
    // Пустое значение трактуем как удаление карты для пассажира.
    delete currentMap[passengerId];
  } else {
    currentMap[passengerId] = trimmedValue;
  }

  writeMilesCardsMap(currentMap);
};

const rebindMilesCardPassengerId = (
  prevPassengerId: string,
  nextPassengerId: string,
) => {
  // После создания пассажира в аккаунте переносим карту
  // с временного локального id на серверный id.
  const currentMap = readMilesCardsMap();
  const value = currentMap[prevPassengerId];

  if (!value) {
    return;
  }

  currentMap[nextPassengerId] = value;
  delete currentMap[prevPassengerId];
  writeMilesCardsMap(currentMap);
};

export const convertPassengerToPayload = ({
  passenger,
}: {
  passenger: Passenger;
}): Partial<Airflow.Order.Payload.Passenger> => {
  const isCountryWithoutSeries =
    citizenshipEntity.config.countriesWithoutSeries.includes(
      passenger.citizenship,
    );

  return {
    id: passenger.id,
    first_name: passenger.firstName,
    last_name: passenger.lastName,
    gender: passenger.gender as Airflow.Gender,
    dob: dateUtils.dateToAccountsFormat(passenger.dob),
    citizenship: passenger.citizenship,
    ...(passenger.citizenship === citizenshipEntity.config.KAZAKHSTAN_CODE && {
      national_id: passenger.iin,
    }),
    ...(passenger.citizenship === citizenshipEntity.config.AZERBAIJAN_CODE && {
      fin: passenger.fin,
    }),
    document_number: passenger.document.number,
    document_expires_at: passenger.document.expiresAt
      ? dateUtils.dateToAccountsFormat(passenger.document.expiresAt)
      : undefined,
    ...(!isCountryWithoutSeries && {
      document_series: passenger.document.series,
    }),
    document_issued_by: passenger.document.organization,
    document_issued_date: passenger.document.givenAt
      ? dateUtils.dateToAccountsFormat(passenger.document.givenAt)
      : undefined,
  };
};

export const milesCardsStorageLib = {
  AIRLINE_BONUS_CARDS_STORAGE_KEY,
  getMilesCardNumberByPassengerId,
  upsertMilesCardByPassengerId,
  rebindMilesCardPassengerId,
  removeMilesCardByPassengerId,
};
